import type { EnvironmentConfig } from './EnvironmentConfig';

let environmentConfig: unknown;

export type EnvironmentLoader<T> = () => Promise<T>;

const CONFIG_DOMAIN_SEPARATOR = '_';
const DEFAULT_COUNTRY = 'AT';
const DOT_REGEXP = /\./gi;
const ANDROID_LOCALHOST = '10.0.2.2';

export function isLocalhost(hostname: string): boolean {
    return ['localhost', '127.0.0.1', ANDROID_LOCALHOST].includes(hostname);
}

export function getConfigFileNameFromLocation(location: Location): string {
    const searchParams = new URLSearchParams(location.search);
    const country =
        searchParams.get('country') || sessionStorage.getItem('country') || DEFAULT_COUNTRY;

    const environment = searchParams.get('environment');
    if (environment) {
        return `${country.toLowerCase()}_${environment}.json`;
    }

    if (isLocalhost(location.hostname)) {
        return `${country.toLowerCase()}_localhost.json`;
    }

    const hostnameParts = location.hostname.split(DOT_REGEXP);
    return `${hostnameParts.reverse().join(CONFIG_DOMAIN_SEPARATOR)}.json`;
}

export async function loadEnv<T = EnvironmentConfig>(load: EnvironmentLoader<T>): Promise<T> {
    try {
        environmentConfig = await load();
        return environmentConfig as T;
    } catch (e: unknown) {
        // logger not initialized yet
        // eslint-disable-next-line no-console,no-restricted-syntax
        console.error('Loading environment config failed', e);
        throw e;
    }
}

export function loadEnvSync<T = EnvironmentConfig>(environment: T): void {
    environmentConfig = environment;
}

/**
 * Retrieves the environment-specific configuration.
 *
 * This function returns an object containing configuration values such as URLs for backends
 * and other systems. The configuration must be loaded using `loadEnv` or `loadEnvSync` before
 * calling this function.
 *
 * @template T - The type of environment configuration. Defaults to EnvironmentConfig.
 * @returns {T} The environment configuration object.
 *
 * @throws {Error} If the environment configuration hasn't been loaded.
 *
 * @example
 * import { getEnv } from '@luna/env';
 *
 * function getGeorgeVersion() {
 *     return getEnv().version;
 * }
 *
 * @example
 * // For retail-specific configuration
 * const retailConfig = getEnv<RetailEnvironmentConfig>();
 *
 * @example
 * // For business-specific configuration
 * const businessConfig = getEnv<BusinessEnvironmentConfig>();
 */
export function getEnv<T = EnvironmentConfig>(): T {
    return environmentConfig as T;
}

export type LicenseInfo = Record<
    string,
    { name: string; license: string; url?: string; content: string | 'NO_LICENSE_PROVIDED' }
>;

/**
 * Retrieves third party licences used in the project
 * WARNING: This works dynamically using bundler (vite/webpack) plugin.
 * When using vite dev mode, due to the partial nature of the compilation,
 * you will obtain only those dependencies that are imported until the point of the call
 */
export async function getThirdPartyLicenses(): Promise<LicenseInfo> {
    const response = await fetch(`/static/third-party-licenses-${process.env.VERSION}.json`);
    return response.json();
}
